/**
 * The HOC version of UserProfileProvider.
 *
 * Passes the user profile as a `profile` prop to the wrapped component.
 * This HOC assumes that your componoent is using exact props.  Please update
 * your component to use exact props before using this HOC.
 *
 * The `profile` prop passed to the wrapped component will be `null` if
 * the profile hasn't finished loading or if there was an error during loading.
 */

import * as React from "react";

import type {ProfileModelType} from "#shared-package/profile-model.ts";

import UserProfileProvider, {
    UserProfileContext,
} from "./user-profile-provider.tsx";

// TODO(WEB-1633): Make all properties required and spread this type in everywhere
export type WithUserProfileProps = {
    profile: ProfileModelType | null;
    profileError?: Error;
    profileLoading?: boolean;
};

export type WithoutUserProfile<T> = Omit<T, keyof WithUserProfileProps>;

const withUserProfile = <
    C extends React.ComponentType<
        React.ComponentProps<C> & WithUserProfileProps
    > & {
        displayName?: string | undefined;
    },
>(
    WrappedComponent: C,
) => {
    type OuterProps = Omit<
        JSX.LibraryManagedAttributes<C, React.ComponentProps<C>>,
        keyof WithUserProfileProps
    >;

    // Try to create a nice displayName for React Dev Tools.
    const displayName =
        WrappedComponent.displayName || WrappedComponent.name || "Component";

    // Creating the inner component. The calculated Props type here is the where the magic happens.
    const ComponentWithBar = (props: OuterProps) => {
        return (
            // eslint-disable-next-line no-restricted-syntax
            <UserProfileProvider>
                {({profile, error, loading}) => (
                    <WrappedComponent
                        {...(props as any)}
                        profile={profile}
                        profileError={error}
                        profileLoading={loading}
                    />
                )}
            </UserProfileProvider>
        );
    };

    ComponentWithBar.displayName = `withUserProfile(${displayName})`;

    return ComponentWithBar;
};

export default withUserProfile;

export {UserProfileContext};
